// src/pages/SentinelAddUser.jsx

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import Layout from "../layouts/Layout";

import {
  API_BASE_URL,
  SKIN_THEMES,
} from "../layouts/constants";

/* =========================================================
   CONSTANTS
========================================================= */


const MENU_CONFIG = [
  {
    key: "dashboard",
    title: "Dashboard",
    path: "/dashboard",
    moduleNames: ["Dashboard"],
  },
  {
    key: "users",
    title: "User Details",
    moduleNames: ["User Details", "Users", "User Management"],
    children: [
      {
        title: "User List",
        path: "/user_list",
        subNames: ["User List"],
      },
      {
        title: "Add New User",
        path: "/add_user",
        subNames: ["Add New User"],
      },
      {
        title: "Sentinel User List",
        path: "/sentinel_user_list",
        subNames: ["Sentinel User List"],
      },
      {
        title: "User Incident Types",
        path: "/add_incidents",
        subNames: ["User Incident Types"],
      },
    ],
  },
  {
    key: "routes",
    title: "Route Management",
    moduleNames: ["Route Management", "Routes"],
    children: [
      {
        title: "Create Route",
        path: "/create_route",
        subNames: ["Create Route"],
      },
      {
        title: "Route List",
        path: "/route_list",
        subNames: ["Route List"],
      },
      {
        title: "Place List",
        path: "/place_list",
        subNames: ["Place List"],
      },
      {
        title: "Sentinel Route List",
        path: "/sentinel_route_list",
        subNames: ["Sentinel Route List"],
      },
    ],
  },
  {
    key: "qr",
    title: "QR Codes",
    moduleNames: ["QR Codes", "QR Code Module", "QR Code", "QR"],
    children: [
      {
        title: "Patrol QR",
        path: "/patrol-qr",
        subNames: ["Patrol QR"],
      },
      {
        title: "Sentinel QR",
        path: "/sentinel-qr",
        subNames: ["Sentinel QR"],
      },
    ],
  },
  {
    key: "installation",
    title: "Installation Module",
    path: "/installation",
    moduleNames: ["Installation Module", "Installation"],
  },
  {
    key: "reports",
    title: "Reporting System",
    moduleNames: ["Reporting System", "Reports"],
    children: [
      {
        title: "Summaries Module",
        path: "/summaries",
        subNames: ["Summaries Module"],
      },
      {
        title: "Analytics Module",
        path: "/analytics",
        subNames: ["Analytics Module"],
      },
      {
        title: "Export Module",
        path: "/export",
        subNames: ["Export Module"],
      },
      {
        title: "Custom Module",
        path: "/custom",
        subNames: ["Custom Module"],
      },
    ],
  },
  {
    key: "sentinel_modules",
    title: "Sentinel Modules",
    moduleNames: [
      "Sentinel Modules",
      "Sentinel Dashboard",
      "Sentinel Reports",
      "InCampus Module",
      "InCampus",
    ],
    children: [
      {
        title: "Sentinel Dashboard",
        path: "/sentinel_dashboard",
        subNames: ["Sentinel Dashboard"],
      },
      {
        title: "Sentinel Reports",
        path: "/sentinel_reports",
        subNames: ["Sentinel Reports"],
      },
      {
        title: "InCampus Module",
        path: "/incampus",
        subNames: ["InCampus Module", "InCampus"],
      },
    ],
  },
];

const ROLE_OPTIONS = [
  "Admin",
  "Supervisor",
  "Guard",
  "Manager",
  "Security",
];

const STATUS_OPTIONS = [
  {
    value: "pending",
    label: "Pending",
  },
  {
    value: "approved",
    label: "Approved",
  },
  {
    value: "disapproved",
    label: "Disapproved",
  },
];

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  designation: "",
  role: "Guard",
  status: "pending",
  username: "",
  password: "",
  guard_seq: "",
  memberId: "",
  year_starts: "",
  year_ends: "",
  dept: "",
  tenant_code: "",
  member_prefix: "",
  cardId: "",
};

const RFID_SCAN_IDLE_MS = 80;
const RFID_MIN_LENGTH = 4;
const RFID_MAX_LENGTH = 32;

/* =========================================================
   GENERAL HELPERS
========================================================= */

function useSkin() {
  const skinId =
    localStorage.getItem(
      "skinTheme"
    ) || "navy";

  return (
    SKIN_THEMES.find(
      (skin) =>
        skin.id === skinId
    ) || SKIN_THEMES[0]
  );
}

/**
 * Add a timestamp to GET URLs so the browser, proxy,
 * or service worker cannot reuse an old response.
 */
function addCacheBuster(
  rawUrl
) {
  const separator =
    rawUrl.includes("?")
      ? "&"
      : "?";

  return `${rawUrl}${separator}_t=${Date.now()}`;
}

/**
 * Read JSON safely.
 */
async function readJsonSafe(
  response
) {
  const contentType =
    response.headers.get(
      "content-type"
    ) || "";

  if (
    contentType.includes(
      "application/json"
    )
  ) {
    return response.json();
  }

  const text =
    await response.text();

  return {
    success: false,
    message:
      text ||
      `Unexpected server response (${response.status})`,
  };
}

/**
 * Perform a GET request without browser caching.
 */
async function fetchNoCache(
  url,
  token
) {
  const response =
    await fetch(
      addCacheBuster(url),
      {
        method: "GET",

        cache: "no-store",

        credentials:
          "same-origin",

        headers: {
          Accept:
            "application/json",

          Authorization:
            `Bearer ${token}`,

          "Cache-Control":
            "no-cache, no-store, must-revalidate",

          Pragma:
            "no-cache",

          Expires:
            "0",

          "X-Requested-With":
            "XMLHttpRequest",
        },
      }
    );

  const data =
    await readJsonSafe(
      response
    );

  return {
    response,
    data,
  };
}

/**
 * Remove an old service worker that may continue returning
 * an older React bundle or cached API responses.
 *
 * Keep this while troubleshooting cache issues.
 */
async function unregisterOldServiceWorkers() {
  if (
    !(
      "serviceWorker" in
      navigator
    )
  ) {
    return;
  }

  try {
    const registrations =
      await navigator
        .serviceWorker
        .getRegistrations();

    for (
      const registration
      of registrations
    ) {
      await registration.unregister();

      console.log(
        "[SentinelAddUser] Old service worker unregistered:",
        registration.scope
      );
    }
  } catch (error) {
    console.warn(
      "[SentinelAddUser] Service worker cleanup failed:",
      error
    );
  }
}

/**
 * Convert an API value into a positive integer or null.
 */
function positiveIntegerOrNull(
  value
) {
  const number =
    Number(value);

  if (
    !Number.isInteger(
      number
    ) ||
    number <= 0
  ) {
    return null;
  }

  return number;
}


function normalizePermissionName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function normalizePermissionPath(value) {
  const path = String(value || "")
    .trim()
    .toLowerCase()
    .split("?")[0]
    .replace(/\/+$/, "");

  return path || "/";
}

function getModuleId(item) {
  return (
    item?.pg_id ??
    item?.page_id ??
    item?.module_id ??
    item?.id ??
    null
  );
}

function getModuleName(item) {
  return (
    item?.modules ||
    item?.module_name ||
    item?.page_name ||
    item?.title ||
    item?.name ||
    ""
  );
}

function getModuleLink(item) {
  return (
    item?.links ||
    item?.module_link ||
    item?.page_link ||
    item?.route_name ||
    item?.path ||
    ""
  );
}

function namesMatch(value, names = []) {
  const normalizedValue = normalizePermissionName(value);

  return names.some(
    (name) =>
      normalizePermissionName(name) === normalizedValue
  );
}

function pathsMatch(value, expectedPath) {
  if (!value || !expectedPath) {
    return false;
  }

  return (
    normalizePermissionPath(value) ===
    normalizePermissionPath(expectedPath)
  );
}

function findApiPage(apiPages, names, path) {
  return apiPages.find((page) => {
    return (
      namesMatch(getModuleName(page), names) ||
      pathsMatch(getModuleLink(page), path)
    );
  });
}

function buildMenuModules(payload) {
  const apiPages = Array.isArray(payload?.modules)
    ? payload.modules.filter(
        (page) => Number(page?.approve ?? 1) === 1
      )
    : [];

  return MENU_CONFIG.map((menuItem) => {
    const hasChildren =
      Array.isArray(menuItem.children) &&
      menuItem.children.length > 0;

    if (!hasChildren) {
      const matchedPage = findApiPage(
        apiPages,
        menuItem.moduleNames || [menuItem.title],
        menuItem.path
      );

      const realPageId = getModuleId(matchedPage);

      return {
        ...(matchedPage || {}),
        key: menuItem.key,
        page_id:
          realPageId !== null
            ? String(realPageId)
            : `missing:${menuItem.key}`,
        modules: menuItem.title,
        links:
          menuItem.path ||
          getModuleLink(matchedPage),
        is_group: false,
        is_available: realPageId !== null,
        permission_page_id:
          realPageId !== null
            ? String(realPageId)
            : null,
        submodules: [],
      };
    }

    const children = menuItem.children.map((child) => {
      const matchedPage = findApiPage(
        apiPages,
        child.subNames || [child.title],
        child.path
      );

      const realPageId = getModuleId(matchedPage);

      return {
        ...(matchedPage || {}),
        subpage_id:
          realPageId !== null
            ? String(realPageId)
            : `missing:${menuItem.key}:${child.path}`,
        permission_page_id:
          realPageId !== null
            ? String(realPageId)
            : null,
        submodules: child.title,
        permission_value: child.title,
        links: child.path,
        is_available: realPageId !== null,
      };
    });

    return {
      key: menuItem.key,
      page_id: `group:${menuItem.key}`,
      modules: menuItem.title,
      links: "",
      is_group: true,
      is_available: children.some(
        (child) => child.is_available
      ),
      submodules: children,
    };
  });
}

function normalizeCardId(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s:_-]+/g, "")
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, RFID_MAX_LENGTH);
}

function isLikelyRfidCardId(value) {
  const cardId = normalizeCardId(value);
  return (
    cardId.length >= RFID_MIN_LENGTH &&
    cardId.length <= RFID_MAX_LENGTH &&
    /^[A-Z0-9]+$/.test(cardId)
  );
}

/* =========================================================
   TOAST HELPERS
========================================================= */

/**
 * Turn a raw backend/network error into a short, friendly
 * message. Raw backend text is never shown to the user directly.
 */
function getFriendlyErrorMessage(rawMessage, context = "save") {
  const message = String(rawMessage || "").toLowerCase();

  if (message.includes("face")) {
    return "Face verification failed. Upload a clear, front-facing photo and try again.";
  }

  if (message.includes("email")) {
    return "This email address is invalid or already in use.";
  }

  if (message.includes("phone")) {
    return "This phone number is invalid or already in use.";
  }

  if (message.includes("username")) {
    return "This username is already taken. Choose another one.";
  }

  if (message.includes("card")) {
    return "This RFID card ID is invalid or already assigned to another user.";
  }

  if (
    message.includes("tenant") ||
    message.includes("licens")
  ) {
    return "There was a problem with the tenant or licensing details. Refresh and try again.";
  }

  if (message.includes("limit")) {
    return "User limit reached. You cannot add more users.";
  }

  if (message.includes("member")) {
    return "There was a problem generating or matching the Member ID. Refresh and try again.";
  }

  if (
    message.includes("401") ||
    message.includes("403") ||
    message.includes("unauthor")
  ) {
    return "Your session has expired. Please log in again.";
  }

  if (context === "load") {
    return "Unable to load the latest data. Refresh the page and try again.";
  }

  return "Something went wrong while saving the user. Please try again.";
}

/* =========================================================
   RFID SCANNER HOOK
========================================================= */

/**
 * USB / HID RFID readers almost always act as a keyboard wedge:
 * they type the card number very quickly and finish with Enter.
 *
 * This hook captures that burst globally (so the operator does
 * not have to click the field first) and writes the Card ID
 * into the form.
 */
function useRfidCardScanner({
  enabled,
  onScan,
}) {
  const bufferRef = useRef("");
  const lastKeyAtRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    function resetBuffer() {
      bufferRef.current = "";
      lastKeyAtRef.current = 0;
    }

    function commitScan() {
      const scanned = normalizeCardId(bufferRef.current);
      resetBuffer();

      if (!isLikelyRfidCardId(scanned)) {
        return;
      }

      onScan(scanned);
    }

    function handleKeyDown(event) {
      const target = event.target;
      const tagName = String(target?.tagName || "").toLowerCase();
      const isEditable =
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        Boolean(target?.isContentEditable);

      const isCardField =
        target?.name === "cardId" ||
        target?.dataset?.rfidField === "cardId";

      /*
        Do not steal typing from normal form fields.
        Only intercept:
        - the Card ID field itself
        - keys that happen when focus is NOT in another input
          (typical when the operator just taps the reader)
      */
      if (isEditable && !isCardField) {
        resetBuffer();
        return;
      }

      const now = Date.now();

      if (
        lastKeyAtRef.current &&
        now - lastKeyAtRef.current > RFID_SCAN_IDLE_MS
      ) {
        bufferRef.current = "";
      }

      lastKeyAtRef.current = now;

      if (event.key === "Enter") {
        if (bufferRef.current) {
          event.preventDefault();
          commitScan();
        }
        return;
      }

      if (event.key === "Escape") {
        resetBuffer();
        return;
      }

      if (event.key.length !== 1) {
        return;
      }

      if (!/[a-zA-Z0-9:_-]/.test(event.key)) {
        return;
      }

      bufferRef.current += event.key;

      if (isCardField) {
        return;
      }

      /*
        Outside the Card ID field, swallow the burst so the
        reader does not dump digits into whatever is focused.
      */
      event.preventDefault();

      if (bufferRef.current.length >= RFID_MAX_LENGTH) {
        commitScan();
      }
    }

    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [enabled, onScan]);
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function SentinelAddUser() {
  const navigate =
    useNavigate();

  useSkin();

  const params =
    new URLSearchParams(
      window.location.search
    );

  const isEdit =
    params.get("mode") ===
    "edit";

  const memberIdFromQuery =
    params.get("member_id") ||
    "";

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    refreshing,
    setRefreshing,
  ] = useState(false);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    dashboardData,
    setDashboardData,
  ] = useState({
    company_name: "",
    tenant_code: "",
    member_prefix: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [
    form,
    setForm,
  ] = useState(
    initialForm
  );

  const [
    guards,
    setGuards,
  ] = useState([]);

  const [
    staffDbConnected,
    setStaffDbConnected,
  ] = useState(true);

  const [
    staffDbError,
    setStaffDbError,
  ] = useState("");

  const [
    payrollEnabled,
    setPayrollEnabled,
  ] = useState(false);

  const [
    modules,
    setModules,
  ] = useState([]);

  const [
    selectedModules,
    setSelectedModules,
  ] = useState({});

  const [
    selectedSubmodules,
    setSelectedSubmodules,
  ] = useState({});

  const [
    faceImage,
    setFaceImage,
  ] = useState(null);

  const [
    facePreview,
    setFacePreview,
  ] = useState("");

  const [
    existingFacePath,
    setExistingFacePath,
  ] = useState("");

  const [
    usePayrollPhoto,
    setUsePayrollPhoto,
  ] = useState(false);

  const [
    fileInputKey,
    setFileInputKey,
  ] = useState(0);

  const [
    licensedUsers,
    setLicensedUsers,
  ] = useState(0);

  const [
    currentUsers,
    setCurrentUsers,
  ] = useState(0);

  const [
    limitReached,
    setLimitReached,
  ] = useState(false);

  const [
    toasts,
    setToasts,
  ] = useState([]);

  const [
    scanReady,
    setScanReady,
  ] = useState(true);

  const [
    lastScannedAt,
    setLastScannedAt,
  ] = useState("");

  const cardInputRef = useRef(null);

  const shouldRequireFaceUpload =
    !isEdit &&
    !faceImage &&
    !usePayrollPhoto;

  /* =======================================================
     TOASTS
  ======================================================= */

  function pushToast(
    type,
    message
  ) {
    const id =
      `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;

    setToasts(
      (previous) => [
        ...previous,
        { id, type, message },
      ]
    );

    setTimeout(() => {
      setToasts(
        (previous) =>
          previous.filter(
            (toast) =>
              toast.id !== id
          )
      );
    }, 5000);
  }

  function dismissToast(id) {
    setToasts(
      (previous) =>
        previous.filter(
          (toast) =>
            toast.id !== id
        )
    );
  }

  function applyScannedCardId(rawCardId) {
    const cardId = normalizeCardId(rawCardId);

    if (!isLikelyRfidCardId(cardId)) {
      pushToast(
        "error",
        "The scanned RFID value is not a valid card ID."
      );
      return;
    }

    setForm((previous) => ({
      ...previous,
      cardId,
    }));

    setLastScannedAt(
      new Date().toLocaleTimeString()
    );

    pushToast(
      "success",
      `RFID card scanned: ${cardId}`
    );

    if (cardInputRef.current) {
      cardInputRef.current.focus();
      cardInputRef.current.select();
    }
  }

  useRfidCardScanner({
    enabled: scanReady && !loading && !saving,
    onScan: applyScannedCardId,
  });

  /* =======================================================
     INITIAL AUTH AND LOAD
  ======================================================= */

  useEffect(() => {
    let active = true;

    async function initializePage() {
      /*
        Remove a previous PWA/service-worker cache.

        This is useful because Ctrl+Shift+R working correctly
        is a strong indication that an older bundle or cached
        GET response may be involved.
      */
      await unregisterOldServiceWorkers();

      if (!active) {
        return;
      }

      const token =
        localStorage.getItem(
          "token"
        ) ||
        localStorage.getItem(
          "authToken"
        );

      const isLoggedIn =
        localStorage.getItem(
          "isLoggedIn"
        );

      if (
        !token ||
        !isLoggedIn
      ) {
        localStorage.clear();

        navigate("/", {
          replace: true,
        });

        return;
      }

      /*
        Local values are used only as a temporary display fallback.

        API values will overwrite them immediately after the
        current request completes.
      */
      let userData = {};

      try {
        userData =
          JSON.parse(
            localStorage.getItem(
              "userData"
            ) || "{}"
          );
      } catch (error) {
        console.error(
          "[SentinelAddUser] Invalid userData:",
          error
        );

        userData = {};
      }

      const localFallback = {
        company_name:
          localStorage.getItem(
            "company_name"
          ) ||
          userData.company_name ||
          "",

        tenant_code:
          localStorage.getItem(
            "tenant_code"
          ) ||
          userData.tenant_code ||
          "",

        member_prefix:
          localStorage.getItem(
            "member_prefix"
          ) ||
          userData.member_prefix ||
          "",

        licensedFor:
          localStorage.getItem(
            "licensedFor"
          ) ||
          userData.licensedFor ||
          "",

        admin_active:
          Number(
            localStorage.getItem(
              "admin_active"
            ) ??
              userData.admin_active ??
              0
          ),

        company_address:
          localStorage.getItem(
            "company_address"
          ) ||
          userData.company_address ||
          "",

        company_phone:
          localStorage.getItem(
            "company_phone"
          ) ||
          userData.company_phone ||
          "",

        admin_email:
          localStorage.getItem(
            "admin_email"
          ) ||
          userData.admin_email ||
          "",

        company_logo:
          localStorage.getItem(
            "company_logo"
          ) ||
          userData.company_logo ||
          "",

        user_map:
          localStorage.getItem(
            "user_map"
          ) ||
          userData.user_map ||
          "",
      };

      if (!active) {
        return;
      }

      setDashboardData(
        localFallback
      );

      await fetchPageData(
        localFallback,
        token,
        false
      );
    }

    initializePage();

    return () => {
      active = false;
    };
  }, [
    navigate,
    isEdit,
    memberIdFromQuery,
  ]);

  /* =======================================================
     PREVIEW URL CLEANUP
  ======================================================= */

  useEffect(() => {
    return () => {
      if (
        facePreview.startsWith(
          "blob:"
        )
      ) {
        URL.revokeObjectURL(
          facePreview
        );
      }
    };
  }, [
    facePreview,
  ]);

  /* =======================================================
     MEMOS
  ======================================================= */

  const routePage =
    useMemo(
      () =>
        dashboardData.user_map ===
        "google"
          ? "/route-master"
          : "/route-master1",
      [
        dashboardData.user_map,
      ]
    );

  const remainingUsers =
    useMemo(() => {
      if (
        licensedUsers > 0
      ) {
        return Math.max(
          0,
          licensedUsers -
            currentUsers
        );
      }

      return null;
    }, [
      licensedUsers,
      currentUsers,
    ]);

  /* =======================================================
     LOCAL STORAGE
  ======================================================= */

  function saveDashboardToStorage(
    companyDetails,
    formPayload,
    localFallback
  ) {
    /*
      API values are preferred over localStorage values.

      This prevents stale local tenant data from overriding
      a newer API response.
    */
    const apiTenantCode =
      String(
        companyDetails
          ?.tenant_code ||
        formPayload
          ?.tenant_code ||
        formPayload
          ?.vm
          ?.tenant_code ||
        ""
      )
        .trim()
        .toUpperCase();

    const apiMemberPrefix =
      positiveIntegerOrNull(
        companyDetails
          ?.member_prefix ||
        formPayload
          ?.member_prefix ||
        formPayload
          ?.vm
          ?.member_prefix
      );

    const tenantCode =
      apiTenantCode ||
      String(
        localFallback
          .tenant_code ||
          ""
      )
        .trim()
        .toUpperCase();

    const memberPrefix =
      apiMemberPrefix ||
      positiveIntegerOrNull(
        localFallback
          .member_prefix
      ) ||
      "";

    const updatedDashboardData = {
      company_name:
        companyDetails
          ?.company_name ||
        formPayload
          ?.company_name ||
        localFallback
          .company_name ||
        "",

      tenant_code:
        tenantCode,

      member_prefix:
        memberPrefix,

      licensedFor:
        companyDetails
          ?.licensedFor ??
        formPayload
          ?.licensedFor ??
        localFallback
          .licensedFor ??
        "",

      admin_active:
        Number(
          companyDetails
            ?.admin_active ??
          formPayload
            ?.admin_active ??
          localFallback
            .admin_active ??
          0
        ),

      company_address:
        companyDetails
          ?.company_address ||
        formPayload
          ?.company_address ||
        localFallback
          .company_address ||
        "",

      company_phone:
        companyDetails
          ?.company_phone ||
        formPayload
          ?.company_phone ||
        localFallback
          .company_phone ||
        "",

      admin_email:
        companyDetails
          ?.admin_email ||
        formPayload
          ?.admin_email ||
        localFallback
          .admin_email ||
        "",

      company_logo:
        companyDetails
          ?.company_logo ||
        formPayload
          ?.company_logo ||
        localFallback
          .company_logo ||
        "",

      user_map:
        companyDetails
          ?.user_map ||
        formPayload
          ?.user_map ||
        localFallback
          .user_map ||
        "",
    };

    let storedUserData = {};

    try {
      storedUserData =
        JSON.parse(
          localStorage.getItem(
            "userData"
          ) || "{}"
        );
    } catch {
      storedUserData = {};
    }

    const updatedUserData = {
      ...storedUserData,
      ...updatedDashboardData,
    };

    localStorage.setItem(
      "userData",
      JSON.stringify(
        updatedUserData
      )
    );

    localStorage.setItem(
      "company_name",
      updatedDashboardData
        .company_name ||
        ""
    );

    localStorage.setItem(
      "tenant_code",
      updatedDashboardData
        .tenant_code ||
        ""
    );

    localStorage.setItem(
      "member_prefix",
      String(
        updatedDashboardData
          .member_prefix ||
        ""
      )
    );

    localStorage.setItem(
      "licensedFor",
      String(
        updatedDashboardData
          .licensedFor ??
        ""
      )
    );

    localStorage.setItem(
      "admin_active",
      String(
        updatedDashboardData
          .admin_active ??
        0
      )
    );

    localStorage.setItem(
      "company_address",
      updatedDashboardData
        .company_address ||
        ""
    );

    localStorage.setItem(
      "company_phone",
      updatedDashboardData
        .company_phone ||
        ""
    );

    localStorage.setItem(
      "admin_email",
      updatedDashboardData
        .admin_email ||
        ""
    );

    localStorage.setItem(
      "company_logo",
      updatedDashboardData
        .company_logo ||
        ""
    );

    localStorage.setItem(
      "user_map",
      updatedDashboardData
        .user_map ||
        ""
    );

    return updatedDashboardData;
  }

  /* =======================================================
     FETCH PAGE DATA
  ======================================================= */

  async function fetchPageData(
    localFallback,
    token,
    manualRefresh = false
  ) {
    try {
      if (manualRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const dashboardUrl =
        `${API_BASE_URL}/dashboard/admin/dashboard`;

      const formUrl =
        `${API_BASE_URL}/sentinel_users/form-init${
          isEdit
            ? `?mode=edit&member_id=${encodeURIComponent(
                memberIdFromQuery
              )}`
            : ""
        }`;

      const [
        dashboardRequest,
        formRequest,
      ] = await Promise.all([
        fetchNoCache(
          dashboardUrl,
          token
        ),

        fetchNoCache(
          formUrl,
          token
        ),
      ]);

      const dashboardRes =
        dashboardRequest.response;

      const dashboardResult =
        dashboardRequest.data;

      const formRes =
        formRequest.response;

      const formResult =
        formRequest.data;

      if (
        dashboardRes.status ===
          401 ||
        dashboardRes.status ===
          403 ||
        formRes.status === 401 ||
        formRes.status === 403
      ) {
        localStorage.clear();

        navigate("/", {
          replace: true,
        });

        return;
      }

      if (!dashboardRes.ok) {
        throw new Error(
          dashboardResult
            ?.message ||
            "Failed to fetch dashboard"
        );
      }

      if (!formRes.ok) {
        throw new Error(
          formResult?.message ||
            "Failed to fetch sentinel form data"
        );
      }

      const companyDetails =
        dashboardResult
          ?.data
          ?.companyDetails ||
        {};

      const payload =
        formResult?.data ||
        {};

      const updatedDashboardData =
        saveDashboardToStorage(
          companyDetails,
          payload,
          localFallback
        );

      setDashboardData(
        updatedDashboardData
      );

      setLicensedUsers(
        Number(
          payload.licensedUsers ||
            0
        )
      );

      setCurrentUsers(
        Number(
          payload.currentUsers ||
            0
        )
      );

      setLimitReached(
        Boolean(
          payload.limitReached
        )
      );

      setPayrollEnabled(
        Boolean(
          payload.payrollEnabled
        )
      );

      setStaffDbError(
        payload.staffDbError ||
          ""
      );

      const guardList =
        Array.isArray(
          payload.guards
        )
          ? payload.guards
          : [];

      setGuards(
        guardList
      );

      setStaffDbConnected(
        payload.staffDbConnected !==
          false
      );

      const modulesWithSubs =
        buildMenuModules(
          payload
        );

      setModules(
        modulesWithSubs
      );

      const vm =
        payload.vm || {};

      const tenantCode =
        String(
          vm.tenant_code ||
          payload.tenant_code ||
          updatedDashboardData
            .tenant_code ||
          ""
        )
          .trim()
          .toUpperCase();

      const memberPrefix =
        positiveIntegerOrNull(
          vm.member_prefix ||
          payload.member_prefix ||
          updatedDashboardData
            .member_prefix
        );

      // Normalize phone: strip +91 / 91 / non-digits, keep last 10 digits for the input
      const rawPhone = String(vm.phone || "").replace(/\D/g, "");
      const normalizedPhone =
        rawPhone.length > 10 &&
        (rawPhone.startsWith("91") || rawPhone.startsWith("0"))
          ? rawPhone.slice(-10)
          : rawPhone.slice(0, 10);

      setForm({
        ...initialForm,

        fullName:
          vm.fullName ||
          "",

        email:
          vm.email ||
          "",

        phone: normalizedPhone,

        designation:
          vm.designation ||
          "",

        role:
          vm.role ||
          "Guard",

        status:
          normalizeStatusValue(
            vm.status ||
              "pending"
          ),

        username:
          vm.username ||
          "",

        password: "",

        guard_seq:
          vm.guard_seq ===
            null ||
          vm.guard_seq ===
            undefined
            ? ""
            : String(
                vm.guard_seq
              ),

        memberId:
          vm.memberId ||
          payload.nextMemberId ||
          "",

        year_starts:
          vm.year_starts ===
            null ||
          vm.year_starts ===
            undefined
            ? ""
            : String(
                vm.year_starts
              ),

        year_ends:
          vm.year_ends ===
            null ||
          vm.year_ends ===
            undefined
            ? ""
            : String(
                vm.year_ends
              ),

        dept:
          vm.dept ===
            null ||
          vm.dept ===
            undefined
            ? ""
            : String(
                vm.dept
              ),

        tenant_code:
          tenantCode,

        member_prefix:
          memberPrefix
            ? String(
                memberPrefix
              )
            : "",

        cardId: normalizeCardId(
          vm.cardId ||
            vm.card_id ||
            vm.rfid_card_id ||
            vm.fld_card_id ||
            ""
        ),
      });

      const savedFacePath =
        vm.face_image_path ||
        "";

      setExistingFacePath(
        savedFacePath
      );

      if (savedFacePath) {
        setFacePreview(
          savedFacePath
        );

        setUsePayrollPhoto(
          false
        );
      } else if (
        vm.guard_seq
      ) {
        const editGuard =
          guardList.find(
            (guard) =>
              String(
                getGuardId(
                  guard
                )
              ) ===
              String(
                vm.guard_seq
              )
          );

        if (
          editGuard
            ?.profile_photo_path
        ) {
          setFacePreview(
            editGuard
              .profile_photo_path
          );

          setUsePayrollPhoto(
            true
          );
        } else {
          setFacePreview("");
          setUsePayrollPhoto(
            false
          );
        }
      } else {
        setFacePreview("");
        setUsePayrollPhoto(
          false
        );
      }

      const existingPermissions =
        payload.existingPermissions ||
        {};

      const modulesState = {};
      const submodulesState = {};

      const hasPermissionAccess =
        (permission) =>
          Boolean(
            permission?.main_access ??
              permission?.approve ??
              permission?.enabled ??
              permission?.access
          );

      modulesWithSubs.forEach(
        (module) => {
          const moduleKey =
            String(module.page_id);

          if (module.is_group) {
            const selectedChildIds =
              module.submodules
                .filter((submodule) => {
                  const permissionId =
                    String(
                      submodule.permission_page_id ||
                        ""
                    );

                  if (!permissionId) {
                    return false;
                  }

                  return hasPermissionAccess(
                    existingPermissions[
                      permissionId
                    ]
                  );
                })
                .map((submodule) =>
                  String(
                    submodule.permission_page_id
                  )
                );

            modulesState[moduleKey] =
              selectedChildIds.length > 0;

            submodulesState[moduleKey] =
              selectedChildIds;

            return;
          }

          const permissionId = String(
            module.permission_page_id ||
              ""
          );

          modulesState[moduleKey] =
            hasPermissionAccess(
              existingPermissions[
                permissionId
              ]
            );

          submodulesState[moduleKey] = [];
        }
      );

      setSelectedModules(
        modulesState
      );

      setSelectedSubmodules(
        submodulesState
      );

      if (manualRefresh) {
        pushToast(
          "success",
          "Latest data refreshed successfully."
        );
      }
    } catch (error) {
      console.error(
        "[SentinelAddUser] Fetch error:",
        error
      );

      pushToast(
        "error",
        getFriendlyErrorMessage(
          error.message,
          "load"
        )
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  async function handleRefresh() {
    const token =
      localStorage.getItem(
        "token"
      ) ||
      localStorage.getItem(
        "authToken"
      );

    if (!token) {
      navigate("/", {
        replace: true,
      });

      return;
    }

    await fetchPageData(
      dashboardData,
      token,
      true
    );
  }

  /* =======================================================
     INPUT HANDLERS
  ======================================================= */

  function handleChange(
    event
  ) {
    const {
      name,
      value,
    } = event.target;

    if (
      name === "guard_seq"
    ) {
      const guard =
        guards.find(
          (item) =>
            String(
              getGuardId(
                item
              )
            ) ===
            String(value)
        );

      const payrollPhoto =
        guard
          ?.profile_photo_path ||
        "";

      if (
        !faceImage &&
        !existingFacePath
      ) {
        if (payrollPhoto) {
          setFacePreview(
            payrollPhoto
          );

          setUsePayrollPhoto(
            true
          );
        } else {
          setFacePreview("");
          setUsePayrollPhoto(
            false
          );
        }
      }

      setForm(
        (previous) => ({
          ...previous,

          guard_seq:
            value,

          fullName:
            guard
              ? getGuardName(
                  guard
                ) ||
                previous
                  .fullName
              : previous
                  .fullName,

          designation:
            guard
              ? guard.designation ||
                guard
                  .fld_designation ||
                previous
                  .designation
              : previous
                  .designation,
        })
      );

      return;
    }

    // Phone: store only digits, max 10 (+91 is fixed in UI / payload)
    if (name === "phone") {
      const digitsOnly = String(value)
        .replace(/\D/g, "")
        .slice(0, 10);

      setForm((previous) => ({
        ...previous,
        phone: digitsOnly,
      }));
      return;
    }

    if (name === "cardId") {
      setForm((previous) => ({
        ...previous,
        cardId: normalizeCardId(value),
      }));
      return;
    }

    setForm(
      (previous) => ({
        ...previous,
        [name]:
          value,
      })
    );
  }

  function handleCardIdKeyDown(event) {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();

    const cardId = normalizeCardId(event.currentTarget.value);

    if (isLikelyRfidCardId(cardId)) {
      applyScannedCardId(cardId);
    }
  }

  function clearCardId() {
    setForm((previous) => ({
      ...previous,
      cardId: "",
    }));
    setLastScannedAt("");
  }

  function isValidEmail(email) {
    const value = String(email || "").trim();
    const atIndex = value.indexOf("@");
    if (atIndex < 1) return false;
    const domain = value.slice(atIndex + 1);
    if (!domain.includes(".")) return false;
    if (/\s/.test(value)) return false;
    if (value.startsWith(".") || value.endsWith(".")) return false;
    if (value.includes("..")) return false;
    return true;
  }

  function isValidPhone(phone) {
    // Exactly 10 digits (excluding fixed +91)
    return /^\d{10}$/.test(String(phone || "").trim());
  }

  function handleFaceImageChange(
    event
  ) {
    const file =
      event.target
        .files?.[0];

    if (!file) {
      return;
    }

    if (
      !file.type.startsWith(
        "image/"
      )
    ) {
      pushToast(
        "error",
        "Please upload a valid image file for the profile photo."
      );

      return;
    }

    if (
      facePreview.startsWith(
        "blob:"
      )
    ) {
      URL.revokeObjectURL(
        facePreview
      );
    }

    setFaceImage(file);

    setFacePreview(
      URL.createObjectURL(
        file
      )
    );

    setUsePayrollPhoto(
      false
    );
  }

  function clearFaceImage() {
    if (
      facePreview.startsWith(
        "blob:"
      )
    ) {
      URL.revokeObjectURL(
        facePreview
      );
    }

    setFaceImage(null);

    setUsePayrollPhoto(
      false
    );

    setFileInputKey(
      (key) =>
        key + 1
    );

    if (
      isEdit &&
      existingFacePath
    ) {
      setFacePreview(
        existingFacePath
      );
    } else {
      setFacePreview("");
    }
  }

  /* =======================================================
     PERMISSIONS
  ======================================================= */

  function toggleModule(module) {
    const moduleKey =
      String(module.page_id);

    if (!module.is_available) {
      pushToast(
        "error",
        "This module is missing from the backend module table. Add the module record first, then refresh."
      );
      return;
    }

    const currentlyEnabled =
      Boolean(
        selectedModules[moduleKey]
      );

    const nextEnabled =
      !currentlyEnabled;

    setSelectedModules(
      (previous) => ({
        ...previous,
        [moduleKey]: nextEnabled,
      })
    );

    if (module.is_group) {
      const availableChildIds =
        module.submodules
          .filter(
            (submodule) =>
              submodule.is_available &&
              submodule.permission_page_id
          )
          .map((submodule) =>
            String(
              submodule.permission_page_id
            )
          );

      setSelectedSubmodules(
        (previous) => ({
          ...previous,
          [moduleKey]: nextEnabled
            ? availableChildIds
            : [],
        })
      );

      return;
    }

    if (!nextEnabled) {
      setSelectedSubmodules(
        (previous) => ({
          ...previous,
          [moduleKey]: [],
        })
      );
    }
  }

  function toggleSubmodule(
    module,
    submodule
  ) {
    if (
      !submodule.is_available ||
      !submodule.permission_page_id
    ) {
      return;
    }

    const moduleKey =
      String(module.page_id);

    const permissionId =
      String(
        submodule.permission_page_id
      );

    setSelectedSubmodules(
      (previous) => {
        const current =
          previous[moduleKey] || [];

        const alreadySelected =
          current.includes(permissionId);

        const next = alreadySelected
          ? current.filter(
              (id) => id !== permissionId
            )
          : [...current, permissionId];

        setSelectedModules(
          (oldModules) => ({
            ...oldModules,
            [moduleKey]:
              next.length > 0,
          })
        );

        return {
          ...previous,
          [moduleKey]: next,
        };
      }
    );
  }

  /* =======================================================
     FORM PAYLOAD
  ======================================================= */

  function buildPayload() {
    const perm_module = {};
    const perm_sub = {};

    modules.forEach((module) => {
      const moduleKey =
        String(module.page_id);

      if (module.is_group) {
        const selectedIds =
          selectedSubmodules[
            moduleKey
          ] || [];

        selectedIds.forEach(
          (permissionId) => {
            if (
              !permissionId ||
              String(permissionId).startsWith(
                "missing:"
              )
            ) {
              return;
            }

            perm_module[
              String(permissionId)
            ] = true;

            perm_sub[
              String(permissionId)
            ] = [];
          }
        );

        return;
      }

      const enabled = Boolean(
        selectedModules[moduleKey]
      );

      const permissionId =
        module.permission_page_id;

      if (!enabled || !permissionId) {
        return;
      }

      perm_module[
        String(permissionId)
      ] = true;

      perm_sub[
        String(permissionId)
      ] = [];
    });

    /*
      Tenant information is displayed in the frontend,
      but the backend must fetch and verify it again from
      admin_vpn_adminLicensing.
    */
    const tenantCode =
      String(
        dashboardData
          .tenant_code ||
        form.tenant_code ||
        ""
      )
        .trim()
        .toUpperCase();

    const payload = {
      form_mode:
        isEdit
          ? "edit"
          : "create",

      fullName:
        form.fullName
          .trim(),

      email:
        form.email
          .trim(),

      // Always store with fixed +91; form.phone is 10 digits only
      phone:
        form.phone.trim()
          ? `+91${form.phone.trim()}`
          : "",

      designation:
        form.designation
          .trim(),

      role:
        form.role
          .trim(),

      status:
        form.status,

      username:
        form.username
          .trim(),

      password:
        form.password,

      guard_seq:
        form.guard_seq ===
          ""
          ? null
          : Number(
              form.guard_seq
            ),

      year_starts:
        form.year_starts ===
          ""
          ? null
          : Number(
              form.year_starts
            ),

      year_ends:
        form.year_ends ===
          ""
          ? null
          : Number(
              form.year_ends
            ),

      dept:
        form.dept ===
          ""
          ? null
          : Number(
              form.dept
            ),

      tenant_code:
        tenantCode,

      cardId: normalizeCardId(form.cardId),
      card_id: normalizeCardId(form.cardId),

      perm_module,
      perm_sub,
    };

    /*
      Only send memberId during edit.

      During create, the backend generates the final Member ID
      inside a SERIALIZABLE transaction.
    */
    if (isEdit) {
      payload.memberId =
        form.memberId
          .trim();
    }

    return payload;
  }

  async function buildFormData() {
    const payload =
      buildPayload();

    if (
      isEdit &&
      !String(
        form.password ||
          ""
      ).trim()
    ) {
      delete payload.password;
    }

    const formData =
      new FormData();

    Object.entries(
      payload
    ).forEach(
      ([
        key,
        value,
      ]) => {
        if (
          value ===
            undefined ||
          value === null
        ) {
          return;
        }

        if (
          key ===
            "perm_module" ||
          key ===
            "perm_sub"
        ) {
          formData.append(
            key,
            JSON.stringify(
              value
            )
          );
        } else {
          formData.append(
            key,
            String(value)
          );
        }
      }
    );

    if (faceImage) {
      formData.append(
        "face_image",
        faceImage
      );
    } else if (
      usePayrollPhoto &&
      facePreview
    ) {
      formData.append(
        "use_payroll_photo",
        "1"
      );

      formData.append(
        "payroll_photo_path",
        facePreview
      );
    }

    return formData;
  }

  /* =======================================================
     VALIDATION
  ======================================================= */

  function getMissingRequiredFields() {
    const missing = [];

    if (!form.fullName.trim()) {
      missing.push("Full Name");
    }

    if (!form.username.trim()) {
      missing.push("Username");
    }

    if (!form.role.trim()) {
      missing.push("Role");
    }

    if (!form.email.trim()) {
      missing.push("Email");
    }

    if (!form.phone.trim()) {
      missing.push("Phone Number");
    }

    if (isEdit && !form.memberId.trim()) {
      missing.push("Member ID");
    }

    if (
      !dashboardData.tenant_code &&
      !form.tenant_code
    ) {
      missing.push("Tenant Code");
    }

    if (
      !dashboardData.member_prefix &&
      !form.member_prefix
    ) {
      missing.push("Member Prefix");
    }

    if (!isEdit && !form.password.trim()) {
      missing.push("Password");
    }

    if (
      !isEdit &&
      !faceImage &&
      !usePayrollPhoto
    ) {
      missing.push("Profile Photo");
    }

    return missing;
  }

  /* =======================================================
     SUBMIT
  ======================================================= */

  async function handleSubmit(
    event
  ) {
    event.preventDefault();

    const missingFields =
      getMissingRequiredFields();

    if (missingFields.length > 0) {
      pushToast(
        "error",
        `Please fill the required field${
          missingFields.length > 1
            ? "s"
            : ""
        }: ${missingFields.join(", ")}`
      );

      return;
    }

    if (!isValidEmail(form.email)) {
      pushToast(
        "error",
        "Please enter a valid email address (must include @ and a domain with .)."
      );
      return;
    }

    if (!isValidPhone(form.phone)) {
      pushToast(
        "error",
        "Phone number must be exactly 10 digits (excluding +91)."
      );
      return;
    }

    if (
      form.cardId &&
      !isLikelyRfidCardId(form.cardId)
    ) {
      pushToast(
        "error",
        "RFID Card ID must be 4–32 letters or digits."
      );
      return;
    }

    if (
      !isEdit &&
      limitReached
    ) {
      pushToast(
        "error",
        `User limit reached (${currentUsers} of ${licensedUsers}). You cannot add more users.`
      );

      return;
    }

    try {
      setSaving(true);

      const token =
        localStorage.getItem(
          "token"
        ) ||
        localStorage.getItem(
          "authToken"
        );

      if (!token) {
        navigate("/", {
          replace: true,
        });

        return;
      }

      const response =
        await fetch(
          `${API_BASE_URL}/sentinel_users/${
            isEdit
              ? "update"
              : "create"
          }`,
          {
            method: "POST",

            cache: "no-store",

            headers: {
              Authorization:
                `Bearer ${token}`,

              "Cache-Control":
                "no-cache, no-store, must-revalidate",

              Pragma:
                "no-cache",

              Expires:
                "0",
            },

            body:
              await buildFormData(),
          }
        );

      const data =
        await readJsonSafe(
          response
        );

      if (
        !response.ok ||
        data.success ===
          false
      ) {
        throw new Error(
          data.message ||
            "Failed to save sentinel user"
        );
      }

      if (isEdit) {
        pushToast(
          "success",
          "User updated successfully!"
        );

        /*
          Refresh edit data directly from the API so old state
          is not displayed.
        */
        await fetchPageData(
          dashboardData,
          token,
          true
        );

        return;
      }

      const createdMemberId =
        data?.data
          ?.memberId ||
        data?.data
          ?.user
          ?.fld_memberId ||
        "";

      const nextMemberId =
        data?.data
          ?.nextMemberId ||
        "";

      pushToast(
        "success",
        createdMemberId
          ? `User added successfully! Member ID: ${createdMemberId}`
          : "User added successfully!"
      );

      setForm({
        ...initialForm,

        status:
          "pending",

        role:
          "Guard",

        tenant_code:
          dashboardData
            .tenant_code ||
          "",

        member_prefix:
          String(
            dashboardData
              .member_prefix ||
            ""
          ),

        memberId:
          nextMemberId,
      });

      setLastScannedAt("");

      setSelectedModules(
        {}
      );

      setSelectedSubmodules(
        {}
      );

      if (
        facePreview.startsWith(
          "blob:"
        )
      ) {
        URL.revokeObjectURL(
          facePreview
        );
      }

      setFaceImage(null);
      setFacePreview("");
      setExistingFacePath("");
      setUsePayrollPhoto(false);

      setFileInputKey(
        (key) =>
          key + 1
      );

      setCurrentUsers(
        (previous) =>
          previous + 1
      );

      /*
        Re-fetch the latest form-init data before leaving.

        This ensures the next Member ID and user count are current.
      */
      await fetchPageData(
        dashboardData,
        token,
        false
      );

      navigate(
        "/sentinel_user_list",
        {
          replace: true,

          state: {
            refresh:
              Date.now(),

            createdMemberId,
          },
        }
      );
    } catch (error) {
      console.error(
        "[SentinelAddUser] Save error:",
        error
      );

      pushToast(
        "error",
        getFriendlyErrorMessage(
          error.message,
          "save"
        )
      );
    } finally {
      setSaving(false);
    }
  }

  /* =======================================================
     LOADING SCREEN
  ======================================================= */

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />

        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading latest sentinel user data...
        </p>
      </div>
    );
  }

  /* =======================================================
     PAGE
  ======================================================= */

  return (
    <Layout
      dashboardData={
        dashboardData
      }
      routePage={
        routePage
      }
      activePage="sentinel_user_list"
      activeMenu="users"
    >
      <ToastStack
        toasts={toasts}
        onDismiss={dismissToast}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-[22px]">
          <div>
            <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
              Sentinel User Management
            </h1>

            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              {isEdit
                ? "Update existing sentinel user details and permissions"
                : "Create a new sentinel user and assign permissions"}
            </p>
          </div>

          <button
            type="button"
            onClick={
              handleRefresh
            }
            disabled={
              refreshing ||
              saving
            }
            className="border border-[var(--accent-border)] bg-[var(--accent-faint)] text-[var(--accent)] rounded-xl px-4 py-2.5 text-[12px] font-extrabold disabled:opacity-60"
          >
            {refreshing
              ? "Refreshing..."
              : "Refresh Latest Data"}
          </button>
        </div>

        <form
          onSubmit={
            handleSubmit
          }
        >
          {/* =================================================
              BASIC INFORMATION
          ================================================= */}

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                  Basic Information
                </h3>

                <p className="text-[12px] text-[var(--text-muted)] m-0 mt-1">
                  {dashboardData
                    .company_name ||
                    "Company"}

                  {dashboardData
                    .tenant_code
                    ? ` (${dashboardData.tenant_code})`
                    : ""}
                </p>
              </div>

              <div className="flex items-stretch gap-2 flex-wrap">
                <div className="rounded-xl bg-[var(--accent-faint)] border border-[var(--accent-border)] px-3.5 py-2">
                  <p className="text-[10px] uppercase tracking-wide font-extrabold text-[var(--text-muted)] m-0">
                    Member Prefix
                  </p>

                  <p className="text-[16px] font-extrabold text-[var(--accent)] m-0 mt-0.5">
                    {dashboardData
                      .member_prefix ||
                      form.member_prefix ||
                      "Not set"}
                  </p>
                </div>

                <div className="rounded-xl bg-[var(--accent-faint)] border border-[var(--accent-border)] px-3.5 py-2">
                  <p className="text-[10px] uppercase tracking-wide font-extrabold text-[var(--text-muted)] m-0">
                    Remaining Users
                  </p>

                  <p className="text-[16px] font-extrabold text-[var(--accent)] m-0 mt-0.5">
                    {remainingUsers ===
                    null
                      ? "Unlimited"
                      : remainingUsers}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Field
                label="Full Name"
                required
              >
                <Input
                  name="fullName"
                  value={
                    form.fullName
                  }
                  onChange={
                    handleChange
                  }
                  required
                />
              </Field>

              <Field
                label="Email"
                required
              >
                <Input
                  type="email"
                  name="email"
                  value={
                    form.email
                  }
                  onChange={
                    handleChange
                  }
                  required
                />
              </Field>

              <Field
                label="Phone"
                required
              >
                <div className="flex items-stretch rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] overflow-hidden focus-within:border-[var(--accent)] focus-within:shadow-[0_0_0_3px_var(--accent-glow)] transition-colors duration-150">
                  <span className="inline-flex items-center px-3.5 text-[13px] font-extrabold text-[var(--text-sub)] bg-[var(--card-bg)] border-r border-[var(--surface-border)] select-none">
                    +91
                  </span>
                  <input
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    value={
                      form.phone
                    }
                    onChange={
                      handleChange
                    }
                    required
                    className="min-w-0 flex-1 border-0 bg-transparent text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] placeholder:text-[var(--text-faint)]"
                    placeholder="10-digit mobile number"
                  />
                </div>
                <p className="mt-1.5 text-[11px] text-[var(--text-muted)] m-0">
                  Country code +91 is fixed. Enter exactly 10 digits.
                </p>
              </Field>

              <Field label="Designation">
                <Input
                  name="designation"
                  value={
                    form.designation
                  }
                  onChange={
                    handleChange
                  }
                />
              </Field>

              <Field
                label="Role"
                required
              >
                <Select
                  name="role"
                  value={
                    form.role
                  }
                  onChange={
                    handleChange
                  }
                  required
                >
                  <option value="">
                    Select role
                  </option>

                  {ROLE_OPTIONS.map(
                    (role) => (
                      <option
                        key={role}
                        value={role}
                      >
                        {role}
                      </option>
                    )
                  )}
                </Select>
              </Field>

              <Field label="Status">
                <Select
                  name="status"
                  value={
                    form.status
                  }
                  onChange={
                    handleChange
                  }
                >
                  {STATUS_OPTIONS.map(
                    (status) => (
                      <option
                        key={
                          status.value
                        }
                        value={
                          status.value
                        }
                      >
                        {
                          status.label
                        }
                      </option>
                    )
                  )}
                </Select>
              </Field>

              <Field label="Year Starts">
                <Input
                  type="number"
                  name="year_starts"
                  value={
                    form.year_starts
                  }
                  onChange={
                    handleChange
                  }
                />
              </Field>

              <Field label="Year Ends">
                <Input
                  type="number"
                  name="year_ends"
                  value={
                    form.year_ends
                  }
                  onChange={
                    handleChange
                  }
                />
              </Field>

              <Field label="Department">
                <Input
                  type="number"
                  name="dept"
                  value={
                    form.dept
                  }
                  onChange={
                    handleChange
                  }
                />
              </Field>

              <Field label="Tenant Code">
                <Input
                  name="tenant_code"
                  value={
                    form.tenant_code ||
                    dashboardData
                      .tenant_code ||
                    ""
                  }
                  readOnly
                  placeholder="Tenant code"
                />
              </Field>

              <Field label="Member Prefix">
                <Input
                  name="member_prefix"
                  value={
                    form.member_prefix ||
                    dashboardData
                      .member_prefix ||
                    ""
                  }
                  readOnly
                  placeholder="Member prefix"
                />
              </Field>
            </div>
          </div>

          {/* =================================================
              RFID CARD
          ================================================= */}

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                  RFID Card
                </h3>

                <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                  Tap the card on the USB RFID scanner. The card number is captured automatically and shown below.
                </p>
              </div>

              <label className="flex items-center gap-2.5 text-[12px] font-bold text-[var(--text-sub)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={scanReady}
                  onChange={(event) =>
                    setScanReady(event.target.checked)
                  }
                  className="w-[18px] h-[18px] accent-[var(--accent)] cursor-pointer"
                />
                Listen for scanner
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Field label="Card ID">
                <div className="flex items-stretch gap-2">
                  <Input
                    ref={cardInputRef}
                    name="cardId"
                    data-rfid-field="cardId"
                    value={form.cardId}
                    onChange={handleChange}
                    onKeyDown={handleCardIdKeyDown}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="Tap RFID card or type ID"
                    className="tracking-[0.08em] font-extrabold"
                  />

                  {form.cardId ? (
                    <button
                      type="button"
                      onClick={clearCardId}
                      className="shrink-0 rounded-xl border border-[var(--red-border)] bg-[var(--red-faint)] px-3 text-[11px] font-extrabold text-[var(--red)]"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>

                <p className="mt-1.5 text-[11px] text-[var(--text-muted)] m-0">
                  {scanReady
                    ? lastScannedAt
                      ? `Last scan at ${lastScannedAt}. Tap another card to replace it.`
                      : "Scanner is ready. Keep this page focused and tap the card."
                    : "Scanner listening is off. Turn it on, or type the Card ID manually."}
                </p>
              </Field>

              <div className="md:col-span-1 xl:col-span-3 flex items-end">
                <div
                  className={`w-full rounded-xl border px-4 py-3 ${
                    form.cardId
                      ? "border-[var(--accent-border)] bg-[var(--accent-faint)]"
                      : "border-dashed border-[var(--surface-border)] bg-[var(--surface)]"
                  }`}
                >
                  <p className="text-[10px] uppercase tracking-wide font-extrabold text-[var(--text-muted)] m-0">
                    Captured card number
                  </p>
                  <p className="text-[18px] font-extrabold tracking-[0.12em] m-0 mt-1 text-[var(--accent)]">
                    {form.cardId || "Waiting for tap…"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* =================================================
              FACE VERIFICATION
          ================================================= */}

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
              Face Verification
            </h3>

            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1 mb-5">
              Upload a clear front-face photo for sentinel face verification
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  {isEdit
                    ? "Guard Face Image"
                    : "Guard Face Image *"}
                </label>

                <input
                  key={
                    fileInputKey
                  }
                  type="file"
                  accept="image/*"
                  onChange={
                    handleFaceImageChange
                  }
                  required={
                    shouldRequireFaceUpload
                  }
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--accent-faint)] file:px-3 file:py-1.5 file:text-[12px] file:font-bold file:text-[var(--accent)]"
                />

                <p className="mt-2 text-[11px] text-[var(--text-muted)]">
                  Use a clear image with only one face. Avoid mask, blur, side face, or dark lighting.
                </p>
              </div>

              <div>
                <p className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Face Preview
                </p>

                {facePreview ? (
                  <>
                    <img
                      src={
                        getImageUrl(
                          facePreview
                        )
                      }
                      alt="Guard face preview"
                      className="h-36 w-36 rounded-xl border border-[var(--surface-border)] object-cover shadow-sm bg-[var(--surface)]"
                      onError={() => {
                        console.log(
                          "Face image failed:",
                          getImageUrl(
                            facePreview
                          )
                        );
                      }}
                    />

                    <p className="mt-1 text-[10px] text-[var(--text-muted)] break-all">
                      {getImageUrl(
                        facePreview
                      )}
                    </p>

                    <button
                      type="button"
                      onClick={
                        clearFaceImage
                      }
                      className="mt-3 rounded-xl border border-[var(--red-border)] bg-[var(--red-faint)] px-3 py-2 text-[11px] font-extrabold text-[var(--red)] hover:bg-[var(--red-border)] transition-colors"
                    >
                      {isEdit &&
                      existingFacePath
                        ? "Reset to Existing Image"
                        : "Remove Image"}
                    </button>
                  </>
                ) : (
                  <div className="h-36 w-36 rounded-xl border border-dashed border-[var(--surface-border)] bg-[var(--surface)] flex items-center justify-center text-[12px] font-bold text-[var(--text-muted)]">
                    No Image
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* =================================================
              LOGIN DETAILS
          ================================================= */}

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-4">
              Login Credentials
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Field
                label="Username"
                required
              >
                <Input
                  name="username"
                  value={
                    form.username
                  }
                  onChange={
                    handleChange
                  }
                  required
                />
              </Field>

              <Field
                label={
                  isEdit
                    ? "Password (leave blank to keep current)"
                    : "Password"
                }
                required={
                  !isEdit
                }
              >
                <Input
                  type="password"
                  name="password"
                  value={
                    form.password
                  }
                  onChange={
                    handleChange
                  }
                  required={
                    !isEdit
                  }
                  placeholder={
                    isEdit
                      ? "Leave blank to keep current"
                      : "Enter password"
                  }
                />
              </Field>

              <Field label="Guard (link to staff)">
                <Select
                  name="guard_seq"
                  value={
                    form.guard_seq
                  }
                  onChange={
                    handleChange
                  }
                >
                  <option value="">
                    -- Select Guard --
                  </option>

                  {!payrollEnabled ? (
                    <option
                      value=""
                      disabled
                    >
                      Payroll is not enabled
                    </option>
                  ) : !staffDbConnected ? (
                    <option
                      value=""
                      disabled
                    >
                      {staffDbError ||
                        "Staff DB connection failed"}
                    </option>
                  ) : (
                    guards.map(
                      (guard) => {
                        const id =
                          getGuardId(
                            guard
                          );

                        const appNo =
                          guard
                            .application_number ||
                          guard
                            .staff_code ||
                          guard
                            .emp_code ||
                          "";

                        const name =
                          getGuardName(
                            guard
                          );

                        const designation =
                          guard
                            .designation ||
                          guard
                            .fld_designation ||
                          "";

                        const hasPhoto =
                          guard
                            .profile_photo_path
                            ? " 📷"
                            : "";

                        const label =
                          `${
                            appNo
                              ? `${appNo} - `
                              : ""
                          }${name}${
                            designation
                              ? ` (${designation})`
                              : ""
                          }${hasPhoto}`.trim();

                        return (
                          <option
                            key={
                              id
                            }
                            value={
                              String(
                                id
                              )
                            }
                          >
                            {label ||
                              id}
                          </option>
                        );
                      }
                    )
                  )}
                </Select>
              </Field>

              <Field label="Member ID">
                <Input
                  name="memberId"
                  value={
                    form.memberId
                  }
                  readOnly
                  placeholder="Auto-generated by backend"
                />

                {!isEdit && (
                  <p className="mt-1 text-[10px] text-[var(--text-muted)]">
                    Preview only. The backend safely generates the final Member ID during creation.
                  </p>
                )}
              </Field>
            </div>
          </div>

          {/* =================================================
              MODULE PERMISSIONS
          ================================================= */}

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-4">
              Module Access & Permissions
            </h3>

            <div className="space-y-3.5">
              {modules.map(
                (module) => {
                  const pageId =
                    String(
                      module.page_id
                    );

                  const enabled =
                    Boolean(
                      selectedModules[
                        pageId
                      ]
                    );

                  const selectedSubs =
                    selectedSubmodules[
                      pageId
                    ] || [];

                  const subs =
                    module.submodules ||
                    [];

                  const moduleAvailable =
                    module.is_available !==
                    false;

                  return (
                    <div
                      key={
                        pageId
                      }
                      className="border border-[var(--surface-border)] rounded-xl overflow-hidden bg-[var(--card-bg)]"
                    >
                      <div
                        className="px-5 py-4 flex justify-between items-center gap-3 flex-wrap"
                        style={{
                          background:
                            "var(--btn-bg)",
                        }}
                      >
                        <div className="text-[15px] font-extrabold text-[var(--btn-text)]">
                          {
                            module.modules
                          }
                        </div>

                        <label className="flex items-center gap-2.5 text-[12px] font-bold text-[var(--btn-text)] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={
                              enabled
                            }
                            disabled={
                              !moduleAvailable
                            }
                            onChange={() =>
                              toggleModule(
                                module
                              )
                            }
                            className="w-[18px] h-[18px] accent-[var(--accent)] cursor-pointer"
                          />

                          Enable Module
                        </label>
                      </div>

                      {!moduleAvailable && (
                        <div className="mx-5 mt-4 rounded-xl border border-[var(--red-border)] bg-[var(--red-faint)] px-4 py-3 text-[12px] font-bold text-[var(--red)]">
                          This module is present in MENU_CONFIG but is missing from the backend modules data. Add it to the module table and refresh.
                        </div>
                      )}

                      <div className="p-5">
                        {subs.length ===
                        0 ? (
                          <div className="py-4 px-4 rounded-xl border border-dashed border-[var(--surface-border)] text-[var(--text-muted)] text-[13px] font-semibold text-center">
                            No sub-modules. Enabling grants module access.
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
                            {subs.map(
                              (sub) => {
                                const permissionId =
                                  String(
                                    sub.permission_page_id ||
                                      ""
                                  );

                                const checked =
                                  selectedSubs.includes(
                                    permissionId
                                  );

                                const canSelect =
                                  enabled &&
                                  sub.is_available &&
                                  Boolean(
                                    sub.permission_page_id
                                  );

                                return (
                                  <label
                                    key={`${pageId}-${sub.subpage_id}`}
                                    className={`flex items-start gap-2.5 border border-[var(--surface-border)] rounded-xl p-3 ${
                                      canSelect
                                        ? "bg-[var(--surface)] cursor-pointer hover:bg-[var(--accent-faint)]"
                                        : "bg-[var(--surface)] opacity-50 cursor-not-allowed"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      disabled={
                                        !canSelect
                                      }
                                      checked={
                                        checked
                                      }
                                      onChange={() =>
                                        toggleSubmodule(
                                          module,
                                          sub
                                        )
                                      }
                                      className="mt-1 w-4 h-4 accent-[var(--accent)]"
                                    />

                                    <div>
                                      <div className="text-[13px] font-bold text-[var(--text-primary)]">
                                        {
                                          sub.submodules
                                        }
                                      </div>

                                      {!sub.is_available && (
                                        <div className="mt-1 text-[10px] text-[var(--red)]">
                                          Missing from backend module data
                                        </div>
                                      )}
                                    </div>
                                  </label>
                                );
                              }
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </div>

          {/* =================================================
              ACTION BUTTONS
          ================================================= */}

          <div className="flex justify-end gap-3 flex-wrap mt-1.5 mb-3">
            <button
              type="button"
              onClick={() =>
                navigate(
                  "/sentinel_user_list"
                )
              }
              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-5 py-3 text-[13px] font-extrabold"
            >
              Back to User List
            </button>

            <button
              type="submit"
              disabled={
                saving ||
                refreshing ||
                (!isEdit &&
                  limitReached)
              }
              className="border-0 rounded-xl px-5 py-3 text-[13px] font-extrabold disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                background:
                  "var(--btn-bg)",

                color:
                  "var(--btn-text)",

                boxShadow:
                  "0 4px 18px var(--accent-glow)",
              }}
            >
              {saving
                ? "Saving..."
                : isEdit
                  ? "Update User"
                  : "+ Create User"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

/* =========================================================
   UI HELPERS
========================================================= */

function ToastStack({
  toasts,
  onDismiss,
}) {
  if (!toasts.length) {
    return null;
  }

  return (
    <div className="fixed top-5 right-5 z-[9999] flex flex-col gap-2.5 w-[320px] max-w-[90vw] pointer-events-none">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`pointer-events-auto flex items-start gap-2.5 px-4 py-3 rounded-xl text-[13px] font-bold bg-white shadow-[0_8px_24px_rgba(0,0,0,0.18)] border-l-4 ${
            toast.type === "success"
              ? "border-l-[#22c55e] border-y border-r border-y-[rgba(0,0,0,0.06)] border-r-[rgba(0,0,0,0.06)] text-[#16833f]"
              : "border-l-[var(--red)] border-y border-r border-y-[rgba(0,0,0,0.06)] border-r-[rgba(0,0,0,0.06)] text-[var(--red)]"
          }`}
        >
          <span className="flex-1 leading-snug">
            {toast.message}
          </span>

          <button
            type="button"
            onClick={() =>
              onDismiss(toast.id)
            }
            aria-label="Dismiss notification"
            className="opacity-70 hover:opacity-100 font-extrabold leading-none text-[14px] shrink-0"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function Field({
  label,
  required = false,
  children,
}) {
  return (
    <div>
      <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
        {label}

        {required ? (
          <span className="text-[var(--red)] ml-0.5">
            *
          </span>
        ) : null}
      </label>

      {children}
    </div>
  );
}

const Input = React.forwardRef(function Input(
  {
    className = "",
    ...props
  },
  ref
) {
  return (
    <input
      ref={ref}
      {...props}
      className={`w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)] disabled:opacity-70 read-only:opacity-70 ${className}`}
    />
  );
});

function Select({
  className = "",
  children,
  ...props
}) {
  return (
    <select
      {...props}
      className={`w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] ${className}`}
    >
      {children}
    </select>
  );
}

/* =========================================================
   DATA HELPERS
========================================================= */

function normalizeStatusValue(
  value
) {
  const status =
    String(
      value || ""
    )
      .trim()
      .toLowerCase();

  if (
    status ===
    "approved"
  ) {
    return "approved";
  }

  if (
    status ===
    "disapproved"
  ) {
    return "disapproved";
  }

  return "pending";
}

function getGuardId(
  guard
) {
  return (
    guard.staff_seq ||
    guard.seq ||
    guard.id ||
    guard.staff_id ||
    guard.emp_id ||
    guard.employee_id ||
    guard.fld_staff_id ||
    guard.fld_emp_id ||
    ""
  );
}

function getGuardName(
  guard
) {
  return (
    guard.staff_name ||
    guard.name ||
    guard.employee_name ||
    guard.full_name ||
    guard.fld_name ||
    ""
  );
}

function getImageUrl(
  path
) {
  if (!path) {
    return "";
  }

  const value =
    String(path).trim();

  if (
    value.startsWith(
      "blob:"
    )
  ) {
    return value;
  }

  if (
    value.startsWith(
      "http://"
    ) ||
    value.startsWith(
      "https://"
    )
  ) {
    return value;
  }

  const cleanPath =
    value.startsWith("/")
      ? value
      : `/${value}`;

  /*
    Prefer the configured API host instead of hardcoding
    localhost:4000.
  */
  const apiOrigin =
    String(
      API_BASE_URL || ""
    )
      .replace(
        /\/api\/?$/,
        ""
      )
      .replace(
        /\/$/,
        ""
      );

  return `${apiOrigin}${cleanPath}`;
}